import imgSrc from '@assets/undraw/undraw_loading_re_5axr.svg'
import { AnimatedContentLoader } from '@components'
import { notificationTypesOmittedOnWebSet } from '@constants'
import { useBusinessContext } from '@context'
import {
  collectFromPages,
  fetchNotifications,
  INotification2,
  markAllAsSeen,
  useCurrentUser
} from '@query'
import { motion } from 'framer-motion'
import React, { CSSProperties, forwardRef, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaCog, FaRegCheckCircle } from 'react-icons/fa'
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import styled from 'styled-components'
import { NotificationFeedFilter } from './NotificationFeedFilter'
import { Notification } from './Notifications'

interface Props {
  style?: CSSProperties
  onOpenPreferences?: () => void
}

type NotificationFilter = 'new' | 'all'

export const NotificationFeed = forwardRef<HTMLDivElement, Props>(
  ({ style, onOpenPreferences }, ref) => {
    const [t] = useTranslation()
    const [filter, setFilter] = useState<NotificationFilter>('new')
    const { slug, data: business } = useBusinessContext()
    const queryClient = useQueryClient()

    const filters = useMemo(
      () => ({
        new: filter == 'new' ? true : null
      }),
      [filter]
    )

    const { data: user } = useCurrentUser()
    const { data, isLoading, hasNextPage, fetchNextPage } = useInfiniteQuery({
      queryKey: ['notifications', user?.id, slug, filters],
      initialPageParam: 1,
      queryFn: ({ pageParam = 1 }) =>
        fetchNotifications({ slug }, { page: pageParam, page_size: 10, ...filters }),
      getNextPageParam: lastPage => lastPage.next,
      getPreviousPageParam: lastPage => lastPage.prev
    })
    const notifications = collectFromPages<INotification2>(data)
    const visibleNotifications = useMemo(
      () => notifications.filter(n => !notificationTypesOmittedOnWebSet.has(n.type)),
      [notifications]
    )

    const { mutate: markAsSeen, isPending: isMarkingAsSeen } = useMutation({
      mutationFn: () => markAllAsSeen({ slug }),
      ...{
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['notifications'] })
        }
      }
    })

    return (
      <StyledNotificationFeed ref={ref} style={style}>
        <StyledNotificationsHeader>
          <HeaderLeft>
            <HeaderTitle>{t('notificationFeed.title')}</HeaderTitle>
            {business?.name && <HeaderSubtitle>{business.name}</HeaderSubtitle>}
          </HeaderLeft>
          {onOpenPreferences && (
            <PreferencesButton type="button" onClick={() => onOpenPreferences()}>
              <span>{t('notificationFeed.preferences.button')}</span>
              <FaCog />
            </PreferencesButton>
          )}
        </StyledNotificationsHeader>
        <FilterBar>
          <NotificationFeedFilter value={filter} onFilter={value => setFilter(value)} />
          <MarkAsReadButton type="button" onClick={() => markAsSeen()} disabled={isMarkingAsSeen}>
            <FaRegCheckCircle />
            <span>{t('notificationFeed.markAsRead')}</span>
          </MarkAsReadButton>
        </FilterBar>
        <StyledNotificationsList>
          <AnimatedContentLoader
            isLoading={isLoading}
            isEmpty={visibleNotifications.length === 0}
            isEmptyContent={<CaughtUp />}
          >
            {visibleNotifications.map(n => (
              <Notification key={n.id} notification={n} />
            ))}

            {hasNextPage && (
              <StyledLoadMore onClick={() => fetchNextPage()}>
                <span>{t('notificationFeed.loadMore')}</span>
              </StyledLoadMore>
            )}
          </AnimatedContentLoader>
        </StyledNotificationsList>
      </StyledNotificationFeed>
    )
  }
)

const CaughtUp: React.FC = () => {
  const [t] = useTranslation()

  return (
    <CaughtUpContainer>
      <CaughtUpImg />
      <CaughtUpTitle>{t('notificationFeed.empty')}</CaughtUpTitle>
    </CaughtUpContainer>
  )
}

const StyledNotificationFeed = styled(motion.div).attrs({
  initial: { opacity: 0, y: -30, x: 20, scale: 0.9 },
  animate: { opacity: 1, y: 0, x: -0, scale: 1 },
  exit: { opacity: 0, y: -30, x: 20, scale: 0.9 }
})`
  display: flex;
  flex-direction: column;
  z-index: 9999;
  background: ${({ theme }) => theme.colors.neutralWhite};
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  box-shadow: ${({ theme }) => theme.shadow.md};
  border: 1px solid rgba(0, 0, 0, 0.04);
  height: 500px;
`

const StyledNotificationsHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => `${theme.spacing.sm}rem ${theme.spacing.md}rem`};
`

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  min-width: 0;
`

const HeaderTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutralBlack};
  margin: 0;
  line-height: 1.25;
`

const HeaderSubtitle = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.iconGray};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const PreferencesButton = styled.button`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  max-width: 50%;
  height: 28px;
  margin: -2px -4px 0 0;
  padding: 0 ${({ theme }) => theme.spacing.xs}rem;
  background: transparent;
  color: ${({ theme }) => theme.colors.iconGray};
  font-weight: 500;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  border: none;
  border-radius: 9999px;
  outline: none !important;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &:hover {
    background: ${({ theme }) => theme.colors.neutralGray};
    color: ${({ theme }) => theme.colors.neutralBlack};
  }

  svg {
    flex-shrink: 0;
    font-size: ${({ theme }) => theme.iconSize.xxs}rem;
  }
`

const FilterBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  padding: ${({ theme }) => `${theme.spacing.xs}rem ${theme.spacing.md}rem`};
  background: ${({ theme }) => theme.colors.neutralWhite};
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
`

const MarkAsReadButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
  height: 28px;
  padding: 0 ${({ theme }) => theme.spacing.sm}rem;
  background: ${({ theme }) => theme.colors.neutralGray};
  color: ${({ theme }) => theme.colors.metalGray};
  font-weight: 500;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  border: 1px solid rgba(0, 0, 0, 0.02);
  border-radius: 9999px;
  outline: none !important;
  cursor: pointer;
  white-space: nowrap;
  transition: color 0.15s ease, background 0.15s ease;

  &:hover {
    color: ${({ theme }) => theme.colors.neutralBlack};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }

  svg {
    font-size: ${({ theme }) => theme.iconSize.xxs}rem;
  }
`

const StyledNotificationsList = styled.div`
  flex: 1;
  overflow: auto;
`

const CaughtUpImg = styled.img.attrs({ src: imgSrc })`
  width: 140px;
`

const CaughtUpTitle = styled.h1`
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`

const CaughtUpContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.xxl}rem;
  gap: ${({ theme }) => theme.spacing.lg}rem;
  align-items: center;
  justify-content: center;
`

const StyledLoadMore = styled.button`
  width: 100%;
  background: none;
  outline: none !important;
  border: none;
  display: flex;
  padding: ${({ theme }) => theme.spacing.sm}rem ${({ theme }) => theme.spacing.md}rem;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  color: ${({ theme }) => theme.colors.metalGray};
`
